import type {
  Annotation,
  BundleEntry,
  CarePlan,
  Consent,
  FhirResource,
  PlanDefinition,
  PlanDefinitionAction,
  Provenance,
  Task,
} from 'fhir/r4';
import * as fhirpath from 'fhirpath';
import moment from 'moment';

import * as Constants from '../common/constants';
import * as CarePlanBuilder from '../common/fhir-builders/careplan';
import * as ConsentBuilder from '../common/fhir-builders/consent';
import * as ProvenanceBuilder from '../common/fhir-builders/provenance';
import type FHIRProxyClient from '../common/fhirproxy-client';
import * as FHIRUtils from '../common/fhirutils';

//Get Intervention actions from PlanDef : eg. high-acuity, mdoerate-acuity etc.
//this will be used by UI to display options for RD so they can chhose one.
//and the selcted option is used to create subtasks for the selected intervention
export function getInterventionActions(planDefinition: PlanDefinition) {
  return fhirpath.evaluate(planDefinition, "action.where(id = 'intervention').action");
}

//this occurs after the intake survey is done and the RD determines the intervention option for the member
//eg. selecting a program track like high-acuity.
//updates CarePlan and creates subtasks for selected intervention
export async function planIntervention(
  orgId: string,
  interventionType: string,
  selectedInterventionAction: PlanDefinitionAction, //action node associated to selected Intervention activity of PlanDef. for eg. the High-Acuity action udner intervention
  patientId: string,
  carePlanId: string,
  careTeamId: string,
  interventionTaskId: string, //task associated to Intervention activity of PlanDef
  effectiveDate: string,
  startDate: string,
  actorRef: { reference: string; display: string },
  updatedBy: string,
  fhirSender: FHIRProxyClient
) {
  try {
    const fhirBundle: FhirResource = FHIRUtils.buildEmptyBundle('transaction');
    let bundleEntries: BundleEntry[] = [];

    if (interventionType === 'auto') {
      //build subtasks for diffferent actions on selected Interevention under intervention Task
      const tasksBundle = CarePlanBuilder.buildSubTasks(
        patientId,
        orgId,
        carePlanId,
        careTeamId,
        interventionTaskId,
        selectedInterventionAction,
        startDate,
        updatedBy
      );
      //add these to the intervention bundle
      bundleEntries = tasksBundle.entry!;
    } else {
      //the sub actions of planned intervention are triggered and created under Intervention step
    }

    //Mark careplan assesment activity and associated task complete. set assessment activity outcome
    const { task, carePlan } = await updateCarePlan(carePlanId, selectedInterventionAction.id, updatedBy, fhirSender);
    if (carePlan) {
      bundleEntries.push(FHIRUtils.buildResourceEntry(carePlan, false));
    }
    if (task) {
      bundleEntries.push(FHIRUtils.buildResourceEntry(task, false));
    }

    const provenancce: Provenance = ProvenanceBuilder.build(
      `CarePlan/${carePlan.id!}`,
      actorRef,
      patientId,
      'UPDATE',
      `Intake/Assessment:Intervention-${selectedInterventionAction.id}`,
      { code: orgId, display: orgId }
    );
    bundleEntries.push(FHIRUtils.buildResourceEntry(provenancce, false));

    const fiConsent: Consent = ConsentBuilder.build(patientId, effectiveDate, updatedBy);
    bundleEntries.push(FHIRUtils.buildResourceEntry(fiConsent, false));

    //now post bundle request
    fhirBundle.entry = bundleEntries;
    const response = await fhirSender.send('post', '', fhirBundle);

    return response;
  } catch (error) {
    console.log('Error in assess:interveneForFoodInsecurity():', (error instanceof Error ? error.message : String(error)));
    throw error;
  }
}
//Update Care Plan Assessment Activity and associated Task to be Complete
//set Assessment activity outcome to selected intervention
async function updateCarePlan(
  carePlanId: string,
  selectedInterventionActionId: string | undefined,
  updatedBy: string,
  fhirSender: FHIRProxyClient
) {
  let task: Task | undefined, carePlan: CarePlan;
  const carePlanResponse = await fhirSender.send<CarePlan>(
    'get',
    'CarePlan/' + carePlanId,
    undefined,
  );
  carePlan = carePlanResponse.data;

  const activities = carePlan.activity;
  let assessmentTaskId;
  const updatedActivities = activities!.map((activity) => {
    if (activity.reference!.display === Constants.ASSESSMENT_ACTIVITY) {
      activity.outcomeReference = [{ display: selectedInterventionActionId }];
      const progress = activity.progress ?? [];
      const annotation: Annotation = {
        text: Constants.ACTIVITY_COMPLETED, //mark careplan activity complete
        time: moment().toISOString(),
        authorString: updatedBy,
      };
      progress.push(annotation);
      activity.progress = progress;

      //get the task for the assessment activity
      if (activity.reference && activity.reference.reference) {
        const taskRef = activity.reference.reference;
        assessmentTaskId = taskRef.substring(taskRef.indexOf('/') + 1);
      }
    }
    return activity;
  });

  carePlan.activity = updatedActivities;
  const identifiers = carePlan.identifier ? carePlan.identifier : [];
  //if the identifier already has selected-intervention data, then we want to overwrite with the selectedInterventionActionId
  if (
    identifiers.findIndex((obj) => obj.system === 'https://projectwell.io/fhir/identifiers/selected-intervention') !==
    -1
  ) {
    const index = identifiers.findIndex(
      (obj) => obj.system === 'https://projectwell.io/fhir/identifiers/selected-intervention'
    );
    identifiers[index]!.value = selectedInterventionActionId;
  } else {
    identifiers.push({
      system: 'https://projectwell.io/fhir/identifiers/selected-intervention',
      value: selectedInterventionActionId,
    });
  }

  carePlan.identifier = identifiers;
  carePlan = FHIRUtils.addUpdatedBy(carePlan, updatedBy);

  //get the assessment task
  const taskResponse = await fhirSender.send<Task>('get', 'Task/' + assessmentTaskId, undefined);
  task = taskResponse.data;
  if (task) {
    task.status = Constants.TASK_STATUS_COMPLETED; //mark associated task complete
    task = FHIRUtils.addUpdatedBy(task, updatedBy);
  }

  return { task, carePlan };
}
